"use client";

import { useTranslations } from "next-intl";
import FounderSeal from "@/components/ui/FounderSeal";

interface FounderIconProps {
  size?: number;
  /** Vertical nudge to optically align with surrounding text baseline */
  nudge?: number;
}

/**
 * Gold "Founding Member" seal, shown BEFORE the green VerifiedIcon next to a
 * founding member's name across the directory. Mirrors VerifiedIcon's a11y
 * (focusable, tooltip + sr-only label, localized). The seal path is the
 * `public/founding.svg` shape inlined so it inherits the gold via
 * `fill="currentColor"` and ships no extra request next to every name.
 */
export default function FounderIcon({ size = 16, nudge = 1 }: FounderIconProps) {
  const t = useTranslations("a11y");
  return (
    <span
      tabIndex={0}
      className="group/founder relative inline-flex shrink-0"
      style={{ translate: `0 ${nudge}px`, paddingTop: 2 }}
      aria-label={t("foundingMember")}
    >
      <FounderSeal size={size} className="text-gold" />

      {/* Tooltip */}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 mb-5 -translate-x-1/2 whitespace-nowrap rounded-md bg-navy px-[7px] py-3 font-sans text-[9px] font-semibold uppercase tracking-widest text-white opacity-0 shadow-e3 transition-opacity duration-150 group-hover/founder:opacity-100 group-focus-within/founder:opacity-100"
      >
        {t("foundingMember")}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-full -translate-x-1/2 border-[3px] border-transparent border-t-navy"
        />
      </span>

      <span className="sr-only">{t("foundingMember")}</span>
    </span>
  );
}
